import React ,  { useState, useCallback , useMemo} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { noop , flatMap , find, filter, get, forEach, cloneDeep} from "lodash";
import { forkJoin } from "rxjs";

import membersService from "@services/members.service";
import { routeMap } from "@utils/constants";
import memberDetailsService from "@services/memberDetails.service";
import { useDurableStore } from "@stores/useDurableStore";
import { useCareCoordinationStore } from "@stores/useCareCoordinationStore";
import { getValidations, isSubTaskDisplayed } from "@pages/member-details/care-coordination/helpers";
import { useMemberListStore } from "@stores/useMemberListStore";
import { SortData, defaultSortData as defaultSort} from "@utils/constants";
import { useGlobalInformationStore } from "@stores/global_informationStore";
import useSession from "./useSession";
import { milleniumClientCheck } from "./helpers";
import moment from "moment-timezone";
import { useCadenceContext } from "@components/drawerOutreaches/useCadence";
import { notification } from "antd";

interface UseDropdownsReturn {
  loadingOptions: boolean;
  getOptions: (value: string , formattingKey ?: string , additionalData ?: any) => void;
  filterDropdowns: any;
}

export const useNavigateToMemberdetails = () => {
  const navigate = useNavigate();

  const goToMemberDetails = React.useCallback(({ patientId, condition = true , fromPage = '' , fromURL = routeMap.dashboard , setLoader = noop}) => {
    if (condition) {
      setLoader(true);
      membersService.membersByIds([patientId]).subscribe({
        next: (v) => {
          let member = v?.data?.data?.patientList.pop();
          if (member) {
            membersService.setCurrentMember = member;
            membersService.setsingleMember = false;
            navigate(routeMap.memberProfile ,  { state: { from : fromURL, fromLabel : fromPage}});
          } else {
            notification.error({
              message: "Inactive Patient",
              description: "Sorry the patient in Inactive",
              onClick: () => {},
            });
          }
          setLoader(false);
        },
        error: (error) => {
          console.log(error);
        },
      });
    }
  }, [navigate]);

  return { goToMemberDetails };
};

//the useDropdowns hook can be used to fetch the dropdown and store it in filterDropdowns store
export function useDropdowns(): UseDropdownsReturn {
  console.log("useReferralsContext.useDropdowns");
  const filterDropdowns = useDurableStore((state) => state.filterDropdowns);
  const setFilterDropdowns = useDurableStore((state) => state.setFilterDropdowns);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);

  const getOptions = useCallback(
    (value: any , formattingKey , additionalData) => {
      const valuesArray: string[] = Array.isArray(value) ? value : [value];
      const dropdownsToFetch : string[] = [];
      const promises: any[] = [];

      valuesArray.forEach((key: string) => {
        const uniqueKey = key + (additionalData ? JSON.stringify(additionalData) : '');
        if (uniqueKey && !Object.keys(filterDropdowns).includes(uniqueKey)) {
          dropdownsToFetch.push(uniqueKey);
          promises.push(memberDetailsService.fetchAttributeDropdown(key, undefined, undefined, undefined, formattingKey || '' , additionalData));
        }
      });

      if (promises.length === 0) {
        return; // No new requests needed, exit early
      }
      const result = forkJoin(promises);
      const _filterDropdowns: Record<string, any> = {};
      setLoadingOptions(true);

      result.subscribe({
        next: (response: any[]) => {
          console.log("success");
          setLoadingOptions(false);
          response.forEach((res: any, index: number) => {
            _filterDropdowns[dropdownsToFetch[index]] = res.data.data.data;
          });
          setFilterDropdowns(_filterDropdowns);
        },
        error: (error) => {
          console.log("failure");
          setLoadingOptions(false);
          console.log(error);
        },
      });
    },
    [filterDropdowns, setFilterDropdowns]
  );

  return { loadingOptions, filterDropdowns, getOptions };
}

export function useDefaultSortData(): any {
  const memberListRegistryDropDown = useMemberListStore((state) => state.memberListRegistryDropDown);
  const sortData = useGlobalInformationStore((state) => state.sortData);
  const setSortData = useGlobalInformationStore((state) => state.setSortData);
  const adtState = useMemberListStore((state) => state.adtState);

  const defaultSortData = useMemo(() => {
    const memberListDefaultSortedList:any = {}
    forEach(memberListRegistryDropDown, (item:any) => {
      if(item?.defaultProperties?.sort) {
        const {columnKey , order} = item?.defaultProperties?.sort;
        memberListDefaultSortedList[`Member List ${columnKey} ${order}`] = columnKey ? {columnKey , order} : {}
      }
    }) 
    const data = {
      ...defaultSort,
      ...memberListDefaultSortedList
    };
    if (Object.keys(sortData)?.length !== Object.keys(data)?.length) {
      setSortData(data);
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberListRegistryDropDown]);

  const adtObject = useMemo(() => {
    return find(memberListRegistryDropDown, { label: adtState})
  } ,[adtState, memberListRegistryDropDown])

  const memberListDefaultSort = useMemo(() => {
    const _data: any = {};
    forEach(Object.keys(defaultSortData), (item: any) => {
      if (item.includes("Member List")) {
        _data[item] = defaultSortData[item];
      }
    });
    return _data;
  }, [defaultSortData]);

  return { defaultSortData , adtObject , memberListDefaultSort};
}

export function useSorter(appPage: string, item: any, handleChange: any): any {
  const sortData = useGlobalInformationStore((state) => state.sortData);
  const setSortData = useGlobalInformationStore((state) => state.setSortData);

  const { defaultSortData , adtObject , memberListDefaultSort} = useDefaultSortData();

  const pageName = useMemo(() => {
    if (appPage.includes("Member List")) {      
      if(adtObject){
        const {columnKey , order} = adtObject?.defaultProperties?.sort;
        return `Member List ${columnKey} ${order}`;
      } else {
        return "Member List";
      }
    } else {
      return appPage;
    }
  }, [adtObject ,appPage]);

  const { order = "ascend", columnKey = "" } = useMemo(() => {
    return sortData?.[pageName] || {};
  }, [pageName, sortData]);

  const handleSort = React.useCallback(
    (action: "ascend" | "descend") => {
      console.log("Colfilters.handleSort");
      let sorter: SortData = {};
      if (action === order && columnKey === item.dataIndex) {
        if (pageName.includes("Member List")) {
          sorter = {
            ...sortData,
            ...memberListDefaultSort
          };
        } else {
          sorter = { ...sortData, [pageName]: defaultSortData[pageName] };
        }
      } else {
        if (pageName.includes("Member List")) {
          const _memlistSortData = cloneDeep(memberListDefaultSort);
          forEach(Object.keys(_memlistSortData), (obj:any) => {
            _memlistSortData[obj] = {
              order: action, columnKey: item.dataIndex
            }
          })
          sorter = {
            ...sortData,
            ..._memlistSortData
          };
        } else {
          sorter = { ...sortData, [pageName]: { order: action, columnKey: item.dataIndex } };
        }
      }
      setSortData(sorter);
      handleChange(
        defaultSortData?.[pageName]?.order === sorter?.[pageName]?.order && defaultSortData?.[pageName]?.columnKey === sorter?.[pageName]?.columnKey
          ? { columnKey : undefined,  order: undefined }
          : sorter?.[pageName]
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnKey, item.dataIndex, order, pageName, sortData]
  );

  return { pageName, handleSort, order, columnKey };
}

export function useMultilevelSelectValue({ selectedVal = '', data =[]}: any): any {

  const value = useMemo(( ) => {
    const result = flatMap(data, (item) => {
      if (item.label === selectedVal) {
        return [item.value];
      } else if (item.children) {
        const child = find(item.children, { label: selectedVal });
        if (child) {
          return [item.value, child.value];
        }
      }
      return [];
    });
    return result;
  }, [data, selectedVal])

  return {value}
}

//useTaskValidations is used to disable the update button until all validations pass
export function useTaskValidations({activeTask = {}, dependantSubTasks = [], additionalDetails = {} , additionalData = {}} : any) {
  const validations = useCareCoordinationStore((state) => state.validations);

  const getTaskvalidations = React.useCallback((subtask) => {
    //returns the validations for that subtask
    const subtaskValidations = filter(validations, (validation: any) => {
      return (get(subtask, "validations", "") ?? "")
        .split(",")
        .map((item: any) => parseFloat(item))
        .includes(parseFloat(validation.id));
    });
    
    return getValidations(subtaskValidations , activeTask , additionalDetails , additionalData) || {};
  },[activeTask, additionalData, additionalDetails, validations]);

  //displayedSubtasks will return the subtasks that are getting displayed
  const displayedSubtasks = React.useCallback(() => {
    return filter(activeTask.subtasks , (st) => {
      return isSubTaskDisplayed(st , dependantSubTasks)
    })
  },[activeTask.subtasks, dependantSubTasks]);

  const isTaskValidationSuccess =  React.useCallback(() => {

    const _displayedSubtasks = displayedSubtasks();

    //_displayedSubtasksWithValidation will have the subtasks that are getting displayed and has validations 
    const _displayedSubtasksWithValidation = _displayedSubtasks.filter((dst) => dst.validations);

    const _validationCheck = _displayedSubtasksWithValidation.every((dswv) => {
      const validation = getTaskvalidations(dswv);
      /* validationFunc is the function that returns true if the subtask value satisfies its validation. 
      it is used to disable to update button until the validation is satisfied*/
      if(validation.validationFunc) {
        return validation.validationFunc(dswv);
      }
      //if there is no validationFunc for that validation 
      return true;
    })

    //_validationCheck returns true if all the validations that to enable the update button are passed 
    return _validationCheck;
  },[displayedSubtasks, getTaskvalidations])


  const isAdditionalValidationSuccess  = React.useCallback(() => {
    const _validationCheck = additionalDetails.every((a : any) => {
      return a?.validation ? !a?.validation(additionalData[a.subtaskTempId]) : false;
    })
    return _validationCheck;
  },[additionalData, additionalDetails])

  return {getTaskvalidations , isTaskValidationSuccess , isAdditionalValidationSuccess};
}

export function usePatientData() {
  const {patientId , patientName} = useMemo(() => {

    const {patient_id , patient_name} = membersService?.getCurrentMember;

    return {patientId : patient_id , patientName : patient_name};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersService?.getCurrentMember]);

  return {patientId , patientName};
}

export function useOutReaches() {

  const location = useLocation();
  const [outReachesLoader , setOutReachesLoader] = useState<boolean>(false);
  const outReachesData = useMemberListStore((state) => state.outReachesData);
  const setOutReachesData = useMemberListStore((state) => state.setOutReachesData);

  const {patientId} = usePatientData();

  function fetchOutReaches({filters = {} , searchBy = '' , page } : any) {
    setOutReachesLoader(true);
    const level = location.pathname === '/member-detail' ? 'patient' : 'global';
    membersService.getStructuredNotes({ patient_id  : patientId , level , filters , searchBy , page}).subscribe({
      next: (v) => {
        if(page) {
          setOutReachesData({list : [...get(outReachesData , 'list' , []) , ...get(v , 'data.data.result1' ,[])] , count : get(v , 'data.data.result2[0].totalCount' ,0)});
        } else {
          setOutReachesData({list : get(v , 'data.data.result1' ,[]) , count : get(v , 'data.data.result2[0].totalCount' ,0)});
        }
        setOutReachesLoader(false);
      },
      error: (e) => {
        setOutReachesLoader(false);
        console.log(e);
      },
    });
  }

  return {outReachesLoader , outReachesData , setOutReachesData , fetchOutReaches};
}


export function useHelperApiFunctions() {

  const navigate = useNavigate();
  const session = useSession();

  const [patientFetchLoader, setPatientFetchLoader] = useState<boolean>(false);
  const structureData = useDurableStore((state) => state.structureData);
  const setStructureData = useDurableStore((state) => state.setStructureData);
  const [structureLoader, setStructureLoader] = useState<boolean>(false);

  const [notesLoader , setNotesLoader] = useState<boolean>(false);

  const [noteDeleteLoader , setNoteDeleteLoader] = useState<boolean>(false);

  //fetches the member by patienid and navigates to member Profile page
  function fetchMemberAndNavigateToProfile({ patientId, successCallback = noop }: { patientId: string; successCallback?: any }) {
    setPatientFetchLoader(true);
    membersService.membersByIds([patientId]).subscribe({
      next: (v) => {
        let member = v?.data?.data?.patientList.pop();
        if (member) {
          membersService.setCurrentMember = member;
          membersService.setsingleMember = false;
          navigate(routeMap.memberProfile);
          setPatientFetchLoader(false);
          successCallback();
        }
      },
      error: () => {
        setPatientFetchLoader(false);
      },
    });
  }

  function getStructuredForm({ struct_type }: { struct_type: string }) {
    if (!Object.keys(structureData).includes(struct_type)) {
      setStructureLoader(true);
      membersService.getStructuredForm({ struct_type }).subscribe({
        next: (v) => {
          setStructureData({ [struct_type]: v.data.data });
          setStructureLoader(false);
        },
        error: () => {
          setStructureLoader(false);
        },
      });
    }
  }

  const getNotes = ({ isAllCM = false, memberId , page = 1, filter  = {} , isPaginationRequired = true , successCallBack = noop , failureCallBack = noop} : any) => {
    if(memberId) {
      setNotesLoader(true)
      membersService.getNotesForPage({ isAllCM, memberId, page: page, filter , isPaginationRequired}).subscribe({
        next: (res) => {
          setNotesLoader(false);
          successCallBack(get(res , 'data.data.notes' , []));
        },
        error: (err) => {
          setNotesLoader(false);
          failureCallBack();
        },
      });
    }
  };

  const deleteNotes = ({noteId , successCallback = noop, errorCallback = noop} : {noteId : string , successCallback : () => void , errorCallback ?: () => void}) => {
    setNoteDeleteLoader(true);
    membersService.deleteNotes(noteId).subscribe({
      next: () => {
        successCallback();
        setNoteDeleteLoader(false);
      },
      error: () => {
        setNoteDeleteLoader(false);
        errorCallback();
      },
    });
  };

  function updateCurrentMemberData  (patient_id: any, data: any)  {
    let memberList = membersService.getMemberList
    memberList = memberList.map(
      (member: any) => {
        if (member.patient_id === patient_id) {
          member = {
            ...member,
            ...data
          }
        }
        return member
      }
    )
    membersService.setMemberList = memberList

    let currentMember = membersService.getCurrentMember 
    currentMember = {
      ...currentMember,
      ...data
    }

    membersService.setCurrentMember = {...currentMember}

    window.dispatchEvent(new Event('member-details:current_member_update'))

  }

  const {
    listCadence
  } = useCadenceContext()
  function addGlobalEntry({ struct_type, entryData, patient_id, level, successCallBack = noop, errorCallBack = noop }: any) {
    membersService.addGlobalEntry({ struct_type, entryData, patient_id, level }).subscribe({
      next: (v) => {
        listCadence && listCadence()
        successCallBack();
      },
      error: (e) => {
        errorCallBack();
      },
    });
  }

  const careManagerId = get(session, "user.id", "");

  const careManagerName = `${get(session, "user.lastName", "")}, ${get(session, "user.firstName", "")}`;
  return {
    fetchMemberAndNavigateToProfile,
    patientFetchLoader,
    notesLoader,
    getNotes,
    deleteNotes,
    noteDeleteLoader,
    getStructuredForm,
    structureLoader,
    structureData,
    careManagerId,
    careManagerName,
    addGlobalEntry,
    updateCurrentMemberData
  }
};